import React from 'react';
import styled from 'styled-components';
import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { useLanguage } from '../context/LanguageContext';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 40px;
`;

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 16px;
`;

const Loader = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  text-align: center;
`;

const formatDate = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_err) {
    return null;
  }
};

const TermsAndConditions = () => {
  const { sections, loading: sectionsLoading, error: sectionsError } = useTermsAndConditions();
  const { metadata, loading: metadataLoading, error: metadataError } = usePageMetadata('terms');
  const loading = sectionsLoading || metadataLoading;
  const error = sectionsError || metadataError;
  const { isArabic } = useLanguage();

  return (
    <Container>
      <Title>{isArabic ? metadata?.title_ar : metadata?.title_en ?? 'Terms and Conditions'}</Title>
      {metadata?.last_updated && (
        <LastUpdated>{isArabic ? 'آخر تحديث' : 'Last Updated'}: {formatDate(metadata.last_updated)}</LastUpdated>

      )}

      {metadata?.description_en && (
        <Section>
          <Paragraph>{isArabic ? metadata.description_ar : metadata.description_en}</Paragraph>
        </Section>
      )}

      {loading ? (
        <Loader>Loading terms and conditions...</Loader>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : sections.length > 0 ? (
        sections.map(section => (
          <Section key={section.id}>
            <SectionTitle>
              {section.section_order}. {isArabic ? section.title_ar : section.title_en}
            </SectionTitle>
            <Paragraph>{isArabic ? section.content_ar : section.content_en}</Paragraph>
          </Section>
        ))
      ) : (
        <ErrorMessage>No terms and conditions content available.</ErrorMessage>
      )}
    </Container>
  );
};

export default TermsAndConditions;